import { useEffect, useRef } from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { useRouter } from '@tanstack/react-router';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useParams } from '@/services/router/compat';

type ByokFlagGateProps = {
  children: React.ReactNode;
};

/**
 * Gates the LLM configuration routes on the `llm-action-byok` feature flag.
 * Accounts without the flag are redirected to workspace settings with an
 * alert, the same shape as `ProtectedRoute`'s permission denial.
 */
export function ByokFlagGate({ children }: ByokFlagGateProps) {
  const isEnabled = useFeatureFlag(AccountFeatureFlag.LLMActionBYOK);
  const router = useRouter();
  const snackbars = useSnackbars();
  const params = useParams({ strict: false });
  const hasShownSnackbarRef = useRef(false);
  const isDenied = isEnabled !== true;

  useEffect(() => {
    if (isDenied) {
      if (!hasShownSnackbarRef.current) {
        snackbars.create({
          message: 'LLM configuration is not available for this workspace.',
          variant: 'critical',
        });
        hasShownSnackbarRef.current = true;
      }
    } else {
      hasShownSnackbarRef.current = false;
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [isDenied]);

  if (isDenied) {
    const redirectTo = router.buildLocation({
      to: '/workspaces/$workspaceId/settings',
      params: { workspaceId: params.workspaceId ?? 'last' },
    }).href;
    return <SafeNavigate to={redirectTo} options={{ shouldReplaceHistoryEntry: true }} />;
  }

  return <>{children}</>;
}
